import React, { useEffect, useState } from 'react';
import { TextInput as RNTextInput, StyleProp, StyleSheet, TextInputProps, TextStyle } from 'react-native';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { AppColors, getThemeColorHexValue } from '@/components/lib/theme';
import { designSystem } from '@/styles/styles';
import { isAndroid } from '@/utils/constants-platform-specific';

import { ds } from './lib/design.system';

export type InlineTextInputProps = {
    helperLabel?: string;
    hasError?: boolean;
    errorText?: string | React.ReactNode;
    textInputStyle?: StyleProp<TextStyle>;
    testID?: string;
    rightIcon?: React.ReactElement | null;
    selectionColor?: AppColors;
    editable?: boolean;
} & TextInputProps;

export const InlineTextInput = React.forwardRef<RNTextInput, InlineTextInputProps>(
    (
        {
            value,
            onChangeText,
            helperLabel,
            hasError = false,
            errorText,
            textInputStyle,
            testID,
            placeholder,
            rightIcon,
            selectionColor,
            editable,
            ...textProps
        },
        ref
    ) => {
        const [showPlaceholder, setShowPlaceholder] = useState(!value);

        useEffect(() => {
            // on Android, the min width of the input is also determined by the placeholder
            // so we hide the placeholder when there is a value
            if (!isAndroid) return;
            if (!value) {
                const timeout = setTimeout(() => setShowPlaceholder(true), 100);
                return () => clearTimeout(timeout);
            }
            setShowPlaceholder(false);
        }, [value]);

        const displayError = hasError && !!errorText;
        const error =
            typeof errorText === 'string' ? (
                <Text variant="bodySmall" color={ds.colorToken.text.danger} style={styles.errorText}>
                    {errorText}
                </Text>
            ) : (
                errorText
            );

        return (
            <Box style={styles.container}>
                <Box style={styles.inputRow}>
                    <RNTextInput
                        ref={ref}
                        value={value}
                        onChangeText={onChangeText}
                        autoCapitalize="none"
                        style={[styles.input, textInputStyle]}
                        selectionColor={getThemeColorHexValue(selectionColor ?? ds.colorToken.text.primary)}
                        selectionHandleColor={getThemeColorHexValue(selectionColor ?? ds.colorToken.text.primary)}
                        // on Android, the min width of the input is also determined by the placeholder
                        // so we hide the placeholder when there is a value
                        placeholder={isAndroid && !showPlaceholder ? '' : placeholder}
                        placeholderTextColor={designSystem.colors.neutral400}
                        keyboardAppearance={'dark'}
                        testID={testID}
                        editable={editable}
                        {...textProps}
                    />
                    {rightIcon}
                </Box>

                {!!helperLabel && !hasError ? (
                    <Text variant="bodySmall" style={styles.helperLabel}>
                        {helperLabel}
                    </Text>
                ) : displayError ? (
                    error
                ) : (
                    //Prevent UI jumps - text height 20, margin top 2 = 22
                    <Box height={22} />
                )}
            </Box>
        );
    }
);

const styles = StyleSheet.create({
    container: {
        width: '100%',
        alignItems: 'center',
    },
    inputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    input: {
        fontSize: 22,
        fontWeight: '700',
        letterSpacing: -0.26,
        color: ds.colorValue.text.primary,
    },
    helperLabel: {
        marginTop: 2,
        letterSpacing: -0.08,
        height: 20,
        color: designSystem.colors.neutral300,
    },
    errorText: {
        marginTop: 2,
        height: 20,
    },
});
